import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE } from '../../config';

// ============================================================
// GLOBAL FILTER TYPES
// ============================================================
export interface GlobalFilters {
  cooConfidence: string[];    // ['HIGH', 'MODERATE', 'LOW'] — multi-select
  country: string[];          // ['United States', 'India', 'China'] — multi-select
  riskLevel: string[];        // ['high', 'medium', 'low'] — multi-select
  manufacturer: string[];     // manufacturer names — multi-select
  shortageStatus: string;     // 'active' | 'resolved' | 'none' | ''
  baaEligible: string;        // 'yes' | 'no' | ''
  ccpFlag: string;            // 'yes' | 'no' | ''
}

export interface FilterOptions {
  countries: string[];
}

// ============================================================
// FILTER APPLICABILITY MAP
// Which filters apply to which pages
// ============================================================
export const FILTER_APPLICABILITY: Record<string, (keyof GlobalFilters)[]> = {
  '/':                  ['cooConfidence', 'country', 'riskLevel', 'shortageStatus'],
  '/products':          ['cooConfidence', 'country', 'riskLevel', 'manufacturer', 'shortageStatus'],
  '/manufacturers':     ['cooConfidence', 'country', 'manufacturer', 'ccpFlag'],
  '/country-origins':   ['cooConfidence', 'country'],
  '/china-dependency':  ['cooConfidence', 'country'],
  '/geopolitical':      ['cooConfidence', 'country'],
  '/shortage-warning':  ['shortageStatus', 'country'],
  '/apis':              ['country'],
};

// ============================================================
// FILTER LABELS (human-readable)
// ============================================================
export const FILTER_LABELS: Record<keyof GlobalFilters, string> = {
  cooConfidence: 'COO Confidence',
  country: 'Country',
  riskLevel: 'Risk Level',
  manufacturer: 'Manufacturer',
  shortageStatus: 'Shortage Status',
  baaEligible: 'BAA Eligibility',
  ccpFlag: 'CCP Flag',
};

// ============================================================
// DEFAULTS — all off (show everything)
// ============================================================
export const DEFAULT_FILTERS: GlobalFilters = {
  cooConfidence: [],
  country: [],
  riskLevel: [],
  manufacturer: [],
  shortageStatus: '',
  baaEligible: '',
  ccpFlag: '',
};

// ============================================================
// COO tier mapping: simplified tier → raw backend tiers
// Used by Products page to map global filter values to API params
// ============================================================
export const COO_TIER_MAP: Record<string, string[]> = {
  HIGH: ['NEAR_CERTAIN', 'HIGH'],
  MODERATE: ['MODERATE_HIGH', 'MODERATE'],
  LOW: ['LOW_MODERATE', 'LOW', 'INSUFFICIENT'],
};

// ============================================================
// CONTEXT TYPE
// ============================================================
interface GlobalFilterContextValue {
  filters: GlobalFilters;
  setFilter: <K extends keyof GlobalFilters>(key: K, value: GlobalFilters[K]) => void;
  clearAll: () => void;
  clearFilter: (key: keyof GlobalFilters) => void;
  applicableFilters: (keyof GlobalFilters)[];
  hasActiveFilters: boolean;
  activeFilterCount: number;
  otherPageFilterCount: number;
  filterOptions: FilterOptions;
}

// ============================================================
// CONTEXT
// ============================================================
const GlobalFilterContext = createContext<GlobalFilterContextValue | null>(null);

// ============================================================
// PROVIDER
// ============================================================
export function GlobalFilterProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [filters, setFilters] = useState<GlobalFilters>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ countries: [] });

  // Fetch filter options on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/metasupply/products/countries`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setFilterOptions(prev => ({ ...prev, countries: data.countries || [] }));
        }
      } catch { /* silently fail */ }
    })();
  }, []);

  // Determine applicable filters for current page
  const getApplicableFilters = useCallback((): (keyof GlobalFilters)[] => {
    const path = location.pathname;
    // Exact match first
    if (FILTER_APPLICABILITY[path]) return FILTER_APPLICABILITY[path];
    // Prefix match for nested routes (e.g., /data-analyst/coo_scoring)
    for (const [route, applicableFilters] of Object.entries(FILTER_APPLICABILITY)) {
      if (route !== '/' && path.startsWith(route)) return applicableFilters;
    }
    // Default: no filters applicable
    return [];
  }, [location.pathname]);

  const applicableFilters = getApplicableFilters();

  const setFilter = useCallback(<K extends keyof GlobalFilters>(key: K, value: GlobalFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearAll = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const clearFilter = useCallback((key: keyof GlobalFilters) => {
    setFilters(prev => ({
      ...prev,
      [key]: Array.isArray(prev[key]) ? [] : '',
    }));
  }, []);

  // Count active filters
  const activeFilterCount = Object.entries(filters).reduce((count, [, value]) => {
    if (Array.isArray(value) && value.length > 0) return count + 1;
    if (typeof value === 'string' && value) return count + 1;
    return count;
  }, 0);

  const hasActiveFilters = activeFilterCount > 0;

  // Count filters active on other pages (not applicable here)
  const otherPageFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    const isActive = Array.isArray(value) ? value.length > 0 : !!value;
    const isApplicable = applicableFilters.includes(key as keyof GlobalFilters);
    if (isActive && !isApplicable) return count + 1;
    return count;
  }, 0);

  return (
    <GlobalFilterContext.Provider value={{
      filters,
      setFilter,
      clearAll,
      clearFilter,
      applicableFilters,
      hasActiveFilters,
      activeFilterCount,
      otherPageFilterCount,
      filterOptions,
    }}>
      {children}
    </GlobalFilterContext.Provider>
  );
}

// ============================================================
// HOOK
// ============================================================
export function useGlobalFilters(): GlobalFilterContextValue {
  const ctx = useContext(GlobalFilterContext);
  if (!ctx) {
    throw new Error('useGlobalFilters must be used within a GlobalFilterProvider');
  }
  return ctx;
}
